import { forumUsersRead } from '@zealot/shared/apiClient';
import { Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/pages/AuthContext';
import { getFanBadgesByUserID } from '@/pages/api/fan-badges';
import logger from '@/utils/logger';

export default function UserProfileHover({
  userName,
  userPicture,
  userPoints = 0,
  userID,
  brandID,
  joinDate,
  children,
  skipForumUserConversion = false,
  hasBadges = true, // Add this prop to control hover functionality
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [userBadges, setUserBadges] = useState([]);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [badgesFetched, setBadgesFetched] = useState(false);
  const [cardPosition, setCardPosition] = useState({
    top: 'mt-2',
    left: 'left-0',
  });
  const triggerRef = useRef(null);
  const rafRef = useRef(null);
  const { fetchToken } = useAuth();
  const apiClient = useApiClient();

  // Calculate best position for the card. Scroll events can fire more often than
  // the browser paints, so coalesce updates into a single requestAnimationFrame —
  // at most one setCardPosition per painted frame instead of one per scroll event.
  const calculatePosition = () => {
    if (rafRef.current !== null) return;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;

      if (!triggerRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Card dimensions (approximate)
      const cardHeight = 200;
      const cardWidth = 288; // w-72 = 18rem = 288px

      let top = 'mt-2';
      let left = 'left-0';

      // Check if card would go below viewport
      if (triggerRect.bottom + cardHeight > viewportHeight) {
        top = 'bottom-full mb-2';
      }

      // Check if card would go beyond right edge
      if (triggerRect.left + cardWidth > viewportWidth) {
        left = 'right-0';
      }

      setCardPosition({ top, left });
    });
  };

  // Fetch badges when hovered and userID/brandID are available
  useEffect(() => {
    const fetchUserBadges = async () => {
      // Skip if no badges for this brand or other conditions not met
      if (!hasBadges || !isHovered || !userID || !brandID || badgesFetched) {
        return;
      }

      setBadgesLoading(true);

      try {
        const token = await fetchToken();
        let actualUserID = userID;

        // Only convert forum user ID if needed
        if (!skipForumUserConversion) {
          // First, get the actual user ID from the forumUserID
          const forumUserResponse = await forumUsersRead({
            client: apiClient,
            path: { uuid: userID },
          });
          actualUserID = forumUserResponse?.data?.data?.fanUserID ?? forumUserResponse?.data?.fanUserID;

          if (!actualUserID) {
            logger.error(new Error('Could not retrieve actual user ID from forum user'));
            setUserBadges([]);
            return;
          }
        }

        // Fetch user's badge records using the actual user ID
        const userBadgeRecords = await getFanBadgesByUserID(actualUserID, token);

        // The API already returns the badges the user has earned, so we can use them directly
        const earnedBadges = userBadgeRecords?.filter((badge) => badge.isPublic === 1) || [];

        setUserBadges(earnedBadges);
        setBadgesFetched(true);
      } catch (error) {
        logger.error(error);
        setUserBadges([]);
      } finally {
        setBadgesLoading(false);
      }
    };

    fetchUserBadges();
  }, [isHovered, userID, brandID, userPoints, fetchToken, badgesFetched, skipForumUserConversion, hasBadges]);

  // Calculate position when hovered, and keep it pinned to the trigger while
  // the page or the surrounding overflow-y-auto leaderboard scrolls/resizes.
  useEffect(() => {
    if (!isHovered || !hasBadges) return;

    calculatePosition();

    // capture: true so nested scroll containers (the leaderboard column) bubble here too.
    window.addEventListener('scroll', calculatePosition, { passive: true, capture: true });
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('scroll', calculatePosition, { capture: true });
      window.removeEventListener('resize', calculatePosition);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isHovered, hasBadges]);

  // Styling classes for the name element
  const nameClasses =
    'text-gray-700 hover:text-gray-900 transition-all duration-200 hover:bg-gray-100 px-1.5 py-1 rounded-md -mx-1.5 -my-1 font-medium hover:shadow-xs border border-transparent hover:border-gray-300 bg-gray-50/50 hover:bg-gray-100 inline-block';

  // Handle click - just stop propagation, let Link handle navigation
  const handleClick = (e) => {
    e.stopPropagation();
    // Don't prevent default - let Next.js Link handle navigation
  };

  // Always make the name clickable if userID is provided
  const nameContent = userID ? (
    <span
      ref={triggerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      style={{ display: 'inline-block', position: 'relative' }}
    >
      <Link
        href={`/profile/${userID}`}
        onClick={handleClick}
        className={`${nameClasses} hover:underline no-underline`}
        style={{
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 10,
          display: 'inline-block',
          cursor: 'pointer',
          textDecoration: 'none',
        }}
      >
        {children}
      </Link>
    </span>
  ) : (
    <span className={nameClasses}>{children}</span>
  );

  // If no badges exist for this brand, just render clickable name without hover
  if (!hasBadges) {
    return userID ? (
      <Link
        href={`/profile/${userID}`}
        onClick={handleClick}
        className={`${nameClasses} hover:underline no-underline`}
        style={{
          pointerEvents: 'auto',
          display: 'inline-block',
          cursor: 'pointer',
          textDecoration: 'none',
        }}
      >
        {children}
      </Link>
    ) : (
      <span className={nameClasses}>{children}</span>
    );
  }

  return (
    <>
      {/* Trigger element (username) - always clickable */}
      {nameContent}

      {/* Hover card rendered in a portal so it escapes the leaderboard column's
          framer-motion transform + overflow-y-auto clipping. Inline, z-9999
          stays trapped in that stacking context and bleeds behind adjacent rows. */}
      {isHovered &&
        typeof document !== 'undefined' &&
        createPortal(
          <Card
            className={`fixed z-9999 w-72 p-0 shadow-xl border bg-popover text-popover-foreground ${cardPosition.top} ${cardPosition.left} animate-in fade-in-0 zoom-in-95 duration-200`}
            style={{
              position: 'fixed',
              top: triggerRef.current ? triggerRef.current.getBoundingClientRect().bottom + 8 : 'auto',
              left: triggerRef.current ? triggerRef.current.getBoundingClientRect().left : 'auto',
              pointerEvents: 'auto',
              ...(cardPosition.top === 'bottom-full mb-2' && {
                top: triggerRef.current ? triggerRef.current.getBoundingClientRect().top - 8 : 'auto',
                transform: 'translateY(-100%)',
              }),
              ...(cardPosition.left === 'right-0' && {
                left: triggerRef.current ? triggerRef.current.getBoundingClientRect().right - 288 : 'auto',
              }),
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12 shrink-0">
                  <AvatarImage
                    src={userPicture}
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.src = '';
                    }}
                  />
                  <AvatarFallback>
                    {userName
                      ?.split(' ')
                      .map((n) => n[0])
                      .join('')}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-popover-foreground truncate">{userName}</div>

                  {userPoints > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">{userPoints.toLocaleString()} points</div>
                  )}

                  {joinDate && <div className="text-xs text-muted-foreground mt-1">Joined {joinDate}</div>}

                  {/* Badges Section */}
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
                      Badges
                      {badgesLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                    </div>

                    {badgesLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-muted rounded-full animate-pulse"></div>
                        <div className="h-3 w-20 bg-muted rounded animate-pulse"></div>
                      </div>
                    ) : userBadges.length > 0 ? (
                      <div className="space-y-2">
                        {userBadges.slice(0, 3).map((badge) => (
                          <div key={badge.badgeID} className="flex items-center gap-2">
                            <img
                              src={badge.badgeImage}
                              alt={badge.badgeTitle}
                              className="w-6 h-6 rounded-full object-cover border border-border"
                            />
                            <span className="text-sm font-medium text-popover-foreground truncate">
                              {badge.badgeTitle}
                            </span>
                          </div>
                        ))}
                        {userBadges.length > 3 && (
                          <div className="text-xs text-muted-foreground mt-1">+{userBadges.length - 3} more badges</div>
                        )}
                      </div>
                    ) : (
                      !badgesLoading && <div className="text-xs text-muted-foreground">No badges earned yet</div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>,
          document.body,
        )}
    </>
  );
}
